import { Injectable, computed, signal } from '@angular/core';
import {
  AllocationSummaryItem,
  AssignedEquipmentResponse,
  BOOKING_ASSIGNMENT_STEPS,
  BookingAssignmentDetailResponse,
  BookingAssignmentStepId,
  BookingEquipmentPropertyResponse,
  ContainerRow,
  EquipmentRecommendationResponse,
  ManualStepRow,
  RecommendedStepRow,
} from '../models/booking-assignment.model';

/**
 * Holds the wizard state shared across the Recommended / Manual / Final steps so
 * that step components stay decoupled and the basket survives step navigation.
 *
 * Load booking detail via loadBookingDetail(); allocation summary and table rows
 * are then derived automatically from the API response and update in real time
 * as the user selects additional recommendations.
 */
@Injectable({ providedIn: 'root' })
export class BookingAssignmentStateService {
  private readonly _bookingNumber = signal<string>('');
  private readonly _destinationLocationCity = signal<string>('');
  private readonly _maintenanceShopCode = signal<string>('');
  private readonly _currentStep = signal<BookingAssignmentStepId>('recommended');
  private readonly _selectedContainers = signal<ContainerRow[]>([]);
  private readonly _manualRows = signal<ManualStepRow[]>([]);

  // Booking detail API response broken into three signals for granular reactivity.
  private readonly _propertyList = signal<BookingEquipmentPropertyResponse[]>([]);
  private readonly _assignedContainers = signal<AssignedEquipmentResponse[]>([]);
  private readonly _recommendedContainers = signal<EquipmentRecommendationResponse[]>([]);
  private readonly _totalBookedQuantity = signal<number>(0);

  // Equipment numbers the user has additionally selected from the recommendations.
  private readonly _additionalSelectedNumbers = signal<string[]>([]);

  // Originally-assigned equipment numbers the user has unchecked.
  private readonly _deselectedAssignedNumbers = signal<string[]>([]);

  // Rows currently selected in the manual step (live, before committing).
  private readonly _manualSelectedRows = signal<ManualStepRow[]>([]);

  // Eligibility check results loaded in the manual step (persisted across navigation).
  private readonly _manualResultRows = signal<ManualStepRow[]>([]);

  // Snapshot of the landing-page filter bar so selections survive navigation to/from detail.
  private readonly _listPageSnapshot = signal<{ maintenanceShopCode: string } | null>(null);

  readonly bookingNumber = this._bookingNumber.asReadonly();
  readonly destinationLocationCity = this._destinationLocationCity.asReadonly();
  readonly maintenanceShopCode = this._maintenanceShopCode.asReadonly();
  readonly currentStep = this._currentStep.asReadonly();
  readonly selectedContainers = this._selectedContainers.asReadonly();
  readonly manualRows = this._manualRows.asReadonly();
  readonly additionalSelectedNumbers = this._additionalSelectedNumbers.asReadonly();
  readonly deselectedAssignedNumbers = this._deselectedAssignedNumbers.asReadonly();
  readonly manualSelectedRows = this._manualSelectedRows.asReadonly();
  readonly manualResultRows = this._manualResultRows.asReadonly();
  readonly totalBookedQuantity = this._totalBookedQuantity.asReadonly();
  readonly listPageSnapshot = this._listPageSnapshot.asReadonly();

  saveListPageSnapshot(maintenanceShopCode: string): void {
    this._listPageSnapshot.set({ maintenanceShopCode });
  }

  /** True once loadBookingDetail() has been called for the current booking. */
  readonly isBookingDetailLoaded = computed(() => this._propertyList().length > 0);

  readonly hasInvalidBookingConfiguration = computed(() =>
    this._propertyList().some((p) => {
      const type = p.equipmentTypeName?.trim().toLowerCase();
      const height = p.equipmentHeight?.trim().toLowerCase();

      return !type || type === 'null' || p.equipmentSize == null || !height || height === 'null';
    }),
  );

  readonly selectedCount = computed(() => this._selectedContainers().length);
  readonly currentStepIndex = computed(() =>
    Math.max(
      0,
      BOOKING_ASSIGNMENT_STEPS.findIndex((step) => step.id === this._currentStep()),
    ),
  );

  /**
   * Combined rows for the final step table — recommended selections (ContainerRow)
   * merged with manual selections (ManualStepRow mapped to ContainerRow shape).
   */
  readonly finalStepRows = computed((): ContainerRow[] => {
    const assignedNums = this.assignedEquipmentNumbers();

    // Containers the user kept or newly selected from the recommended step.
    const fromRecommended: ContainerRow[] = this._selectedContainers().map((r) => ({
      ...r,
      source: 'Recommended' as const,
      action: assignedNums.has(r.equipmentNumber) ? ('No Action' as const) : ('Added' as const),
    }));

    // Assigned containers the user explicitly unchecked — shown as Removed.
    const removedRows: ContainerRow[] = this._assignedContainers()
      .filter((c) => this._deselectedAssignedNumbers().includes(c.equipmentNumber))
      .map((c) => ({
        equipmentNumber: c.equipmentNumber,
        equipmentType: c.equipmentTypeName,
        grade: c.equipmentGradeCode,
        size: c.equipmentSize.toString(),
        height: BookingAssignmentStateService.normalizeHeight(c.equipmentOutsideHeight),
        source: 'Recommended' as const,
        action: 'Removed' as const,
      }));

    // Containers added from the manual step.
    const seenFromRecommended = new Set(fromRecommended.map((r) => r.equipmentNumber));
    const fromManual: ContainerRow[] = this._manualRows()
      .filter((r) => !seenFromRecommended.has(r.equipmentNumber))
      .map((r) => ({
        equipmentNumber: r.equipmentNumber,
        equipmentType: r.type,
        grade: r.grade,
        size: r.size,
        height: r.height,
        source: 'Manual' as const,
        action: 'Added' as const,
        auditId: r.auditId ?? null,
        remarks: r.remarks ?? null,
        comments: r.comments ?? null,
      }));

    return [...fromRecommended, ...removedRows, ...fromManual];
  });

  /** Set of equipment numbers that were already assigned when the booking was loaded. */
  readonly assignedEquipmentNumbers = computed(() => new Set(this._assignedContainers().map((c) => c.equipmentNumber)));

  /** Combined rows for the recommended step table: pre-assigned first (sorted by sequenceNumber), then recommendations. */
  readonly recommendedStepRows = computed((): RecommendedStepRow[] => {
    const assigned: RecommendedStepRow[] = [...this._assignedContainers()]
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      .map((c) => ({
        equipmentNumber: c.equipmentNumber,
        equipmentType: c.equipmentTypeName,
        grade: c.equipmentGradeCode,
        size: c.equipmentSize.toString(),
        height: BookingAssignmentStateService.normalizeHeight(c.equipmentOutsideHeight),
        sale: '',
        redelivery: '',
        inDepoSince: '',
        isAssigned: true,
      }));
    const assignedNumbers = new Set(assigned.map((r) => r.equipmentNumber));
    const recommended: RecommendedStepRow[] = this._recommendedContainers()
      .filter((c) => !assignedNumbers.has(c.equipmentNumber))
      .map((c) => ({
        equipmentNumber: c.equipmentNumber,
        equipmentType: c.equipmentTypeName,
        grade: c.gradeCode ?? '',
        size: c.sizeCode?.toString() ?? '',
        height: BookingAssignmentStateService.normalizeHeight(c.equipmentHeight ?? ''),
        sale: BookingAssignmentStateService.boolToYesNo(c.isTaggedForSelling),
        redelivery: BookingAssignmentStateService.boolToYesNo(c.isTaggedForRedelivery),
        inDepoSince: c.daysAtLocation === null || c.daysAtLocation === undefined ? '' : `${c.daysAtLocation} days`,
        isAssigned: false,
      }));
    return [...assigned, ...recommended];
  });

  /**
   * Allocation summary — reacts to both the booking property list and the user's
   * in-progress selections (recommended + manual) so the rail updates in real time.
   */
  readonly allocationSummary = computed((): AllocationSummaryItem[] => {
    const additional = this._additionalSelectedNumbers();
    const deselected = this._deselectedAssignedNumbers();
    const additionalRows = this._recommendedContainers().filter((r) => additional.includes(r.equipmentNumber));
    const deselectedRows = this._assignedContainers().filter((c) => deselected.includes(c.equipmentNumber));
    const manualRows = this._manualSelectedRows();
    return this._propertyList().map((p) => {
      const sizeStr = p.equipmentSize.toString();
      const extraFromRecommended = additionalRows.filter(
        (r) => r.equipmentTypeName === p.equipmentTypeName && (r.sizeCode?.toString() ?? '') === sizeStr,
      ).length;
      // Exclude manual rows whose equipment number is already counted via recommended
      const recNums = new Set(additional);
      const extraFromManual = manualRows.filter(
        (r) => !recNums.has(r.equipmentNumber) && r.type === p.equipmentTypeName && r.size === sizeStr,
      ).length;
      const deductedFromAssigned = deselectedRows.filter(
        (c) => c.equipmentTypeName === p.equipmentTypeName && c.equipmentSize.toString() === sizeStr,
      ).length;
      return {
        equipmentType: p.equipmentTypeName,
        size: sizeStr,
        height: p.equipmentHeight,
        hasFoodGrade: BookingAssignmentStateService.checkHasFoodGrade(p.gradeToQuantityMap),
        assignedQuantity: p.assignedQuantity - deductedFromAssigned + extraFromRecommended + extraFromManual,
        bookedQuantity: p.totalQuantity,
      };
    });
  });

  /** Load a full booking detail response and reset per-session selection state. */
  loadBookingDetail(response: BookingAssignmentDetailResponse): void {
    this._propertyList.set(response.equipmentPropertyList ?? []);
    this._assignedContainers.set(response.assignedEquipmentList ?? []);
    this._recommendedContainers.set(response.equipmentRecommendationList ?? []);

    this._totalBookedQuantity.set(response.totalBookedEquipmentQuantity);
    this._destinationLocationCity.set(response.destinationLocationCity ?? '');

    this._additionalSelectedNumbers.set([]);
    this._deselectedAssignedNumbers.set([]);
  }

  /** Called by the recommended step whenever the user's recommendation selection changes. */
  updateAdditionalSelected(equipmentNumbers: string[]): void {
    this._additionalSelectedNumbers.set(equipmentNumbers);
  }

  /** Called by the recommended step when the user unchecks a previously-assigned container. */
  updateDeselectedAssigned(equipmentNumbers: string[]): void {
    this._deselectedAssignedNumbers.set(equipmentNumbers);
  }

  /** Called by the manual step on every checkbox change to keep the summary in sync. */
  updateManualSelected(rows: ManualStepRow[]): void {
    this._manualSelectedRows.set(rows);
  }

  /** Persists the eligibility-check results grid so it survives step navigation. */
  updateManualResultRows(rows: ManualStepRow[]): void {
    this._manualResultRows.set(rows);
  }

  /** Reset all wizard state when navigating to a new booking. */
  reset(bookingNumber: string, maintenanceShopCode: string = ''): void {
    this._bookingNumber.set(bookingNumber);
    this._maintenanceShopCode.set(maintenanceShopCode);
    this._destinationLocationCity.set('');
    this._currentStep.set('recommended');
    this._selectedContainers.set([]);
    this._manualRows.set([]);
    this._propertyList.set([]);
    this._assignedContainers.set([]);
    this._recommendedContainers.set([]);
    this._totalBookedQuantity.set(0);
    this._additionalSelectedNumbers.set([]);
    this._deselectedAssignedNumbers.set([]);
    this._manualSelectedRows.set([]);
    this._manualResultRows.set([]);
  }

  goToStep(step: BookingAssignmentStepId): void {
    this._currentStep.set(step);
  }

  /** Replace the recommended-step selection entirely. Used on every "Next" from the recommended step
   * so that going back and deselecting rows is reflected correctly in the final step. */
  setRecommendedContainers(containers: ContainerRow[]): void {
    this._selectedContainers.set(containers);
  }

  /** @deprecated Use setRecommendedContainers instead. Kept for any legacy call-sites. */
  addContainers(containers: ContainerRow[]): void {
    this.setRecommendedContainers(containers);
  }

  removeContainer(equipmentNumber: string): void {
    this._selectedContainers.update((existing) => existing.filter((row) => row.equipmentNumber !== equipmentNumber));
  }

  /** Store manually-validated rows (with exception comments) and advance to the final step. */
  addManualRows(rows: ManualStepRow[]): void {
    this._manualRows.set(rows);
    this.goToStep('final');
  }

  private static readonly PQC_GRADES = new Set(['S', 'M']);

  /** Returns true if any key in gradeToQuantityMap is a PQC grade. */
  static checkHasFoodGrade(gradeToQuantityMap: Record<string, number>): boolean {
    return Object.keys(gradeToQuantityMap).some((g) => BookingAssignmentStateService.PQC_GRADES.has(g));
  }

  /** Converts API height format "8 6" → "8\"6", "9 6" → "9\"6". Passes through already-formatted values unchanged. */
  static normalizeHeight(raw: string): string {
    return raw.replace(/^(\d+)\s(\d+)$/, '$1"$2');
  }

  /** Converts a nullable boolean to 'Yes' / 'No' / '' for display and filtering. */
  static boolToYesNo(value: boolean | null | undefined): string {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '';
  }
}
